import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivityIndicator,
  Image,
  Keyboard,
  ScrollView,
  StyleSheet,
  Text,
} from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation, useRoute } from '@react-navigation/native'
import { zetProFixedList } from '@zet/common/utils/zetProData'
import { useAddCustomerNextStateWeb } from '@zet/customer/src/api/hooks'
import {
  useAgentDetailsWF,
  useGetCriteriaIndexes,
  useGetZetCashMarketplaceBanner,
} from '@zet/customer/src/api/hooks/webFlow'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B5 } from '@zet/design-system/typography'
import { WebDatePicker } from '@zet/design-system/webDatePicker'
import {
  privacyPolicyLink,
  termsAndConditionsLink,
} from '@zet/feature-brands/utils/constants'
import { i18n } from '@zet/localization'
import { getLoginHash } from '@zet/utils/loginHash'
import {
  MOBILE_NUMBER_REGEX,
  NAME_REGEX,
  PAN_REGEX,
  PINCODE_REGEX,
} from '@zet/utils/regularExpression'
import {
  getUserFlowSession,
  hasUserFlowLoginSession,
  setUserFlowSession,
} from '@zet/utils/userFlowSession'
import {
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '@zet/utils/validationMessage'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Marquee } from '../../../screens/independenceCampaign/components/marquee'
import ScreenViewEventWrapper from '../../components/screenViewEventWrapper'
import { CUSTOMER_DETAILS_OCCUPATION_ICONS } from '../../components/userFlow'
import {
  getUserFlowFallbackRoute,
  getUserFlowInitialValues,
  getUserFlowMode,
  useUserFlowPartnerProducts,
} from '../userFlow/utils'

const userLoginHeroBanner =
  'https://zet-cms-staging.s3.ap-south-1.amazonaws.com/Hero_Section_634de65e74.png'
const userLoginHeroAspectRatio = 1368 / 500
const userLoginPhoneIcon =
  'https://zet-cms-staging.s3.ap-south-1.amazonaws.com/user_phone_10bb62802d.png'
const userLoginRupeeIcon =
  'https://zet-cms-staging.s3.ap-south-1.amazonaws.com/rupee_49616966da.png'
const primaryButtonStyle = {
  backgroundColor: '#832DC2',
  borderColor: '#7929B4',
  borderWidth: 1.2,
  borderRadius: 14,
  minHeight: 48,
}

const partnerCardWidth = 132
const partnerCardGap = 12
const partnerCardHeight = 60
const USER_LOGIN_CUSTOMER_INTENT = 'LOANS'
const OTP_LENGTH = 4
const USER_BASIC_DETAILS_SCREEN = 'SHOWN_BASIC_DETAILS_SCREEN_V2'
const USER_PAN_DETAILS_SCREEN = 'SHOWN_PAN_COLLECTION_SCREEN_V2'
const BASIC_DETAILS_OCCUPATION = {
  SALARIED: 'Salaried',
  SELF_EMPLOYED: 'Self Employed',
} as const
type PartnerCarouselCard = {
  key: string
  logo: string
  width: number
}

const getPartnerLogoWidth = (name?: string) => {
  const normalizedName = (name || '').trim()

  if (!normalizedName) {
    return 92
  }

  return Math.min(Math.max(normalizedName.length * 7.2, 68), 108)
}

type UserLoginRouteParams = {
  customerId?: number
  customerPhoneNumber?: string
  customerName?: string
  screenName?: string
}

const basicDetailsValidationSchema = z.object({
  occupation: z.string().min(1, { message: 'Please select occupation' }),
  monthlyIncome: z
    .string()
    .min(1, { message: 'Monthly income is required' })
    .regex(/^([1-9]\d*)$/, {
      message: 'Please enter valid monthly income',
    }),
  pinCode: z
    .string()
    .min(1, { message: 'Field is required!' })
    .length(6, { message: 'Not a valid PIN code' })
    .regex(PINCODE_REGEX, 'Invalid Pin Code'),
})

const panDetailsValidationSchema = z.object({
  panNumber: z
    .string()
    .min(1, { message: 'Field is required!' })
    .max(10)
    .regex(new RegExp(PAN_REGEX), 'Invalid PAN'),
  dob: z
    .string()
    .regex(
      new RegExp(
        /^(?:(?:(?:0[1-9]|1[0-9]|2[0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4})|(?:(?:\d{4})-(0[1-9]|1[0-2])-(0[1-9]|1[0-9]|2[0-9]|3[01])))$/,
      ),
      'Please select a valid Date',
    ),
})

const formatIncomeValue = (value: string) =>
  value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const normalizeOccupationValue = (value?: string) =>
  value?.toLowerCase().replace(/[\s-]+/g, '') || ''

const inputFrameBaseStyle = {
  minHeight: 58,
  borderRadius: 16,
  backgroundColor: '#FBFAFC',
  fontFamily: 'THICCCBOI-SemiBold',
  fontSize: 16,
  lineHeight: 22,
  letterSpacing: -0.16,
  color: '#141216',
  outlineStyle: 'none',
  outlineWidth: 0,
  boxShadow: 'none',
} as const

const compactInputFrameBaseStyle = {
  minHeight: 48,
  borderRadius: 14,
  borderColor: '#E1DFE5',
  backgroundColor: '#FBFAFC',
  fontFamily: 'THICCCBOI-SemiBold',
  fontSize: 16,
  lineHeight: 22,
  letterSpacing: -0.16,
  color: '#141216',
  outlineStyle: 'none',
  outlineWidth: 0,
  boxShadow: 'none',
} as const

const violetFocusInputClassName = 'focus:border-[#832DC2]'
const otpFocusInputClassName = 'focus:border-[#A855F7]'

const getInputFrameStyle = (
  isFocused: boolean,
): typeof inputFrameBaseStyle & { borderColor: string } => ({
  ...inputFrameBaseStyle,
  borderColor: isFocused ? '#832DC2' : '#E5E7EB',
})

const getOtpInputFrameStyle = (isFocused: boolean): object => ({
  ...styles.otpInputFrame,
  borderColor: isFocused ? '#A855F7' : '#E5E7EB',
})

const getOccupationCardStyle = (
  isSelected: boolean,
): readonly [object, object] => [
  styles.occupationCard,
  isSelected ? styles.occupationCardSelected : styles.occupationCardDefault,
]

const getPartnerLogoStyle = (width?: number) => [
  styles.partnerLogo,
  width ? { width } : styles.partnerLogoDefaultWidth,
]

const UserLogin = () => {
  const navigation = useNavigation()
  const route = useRoute() as {
    name: 'UserLogin' | 'UserOtp' | 'UserBasicDetails' | 'UserPanDetails'
    params?: UserLoginRouteParams
  }
  const isOtpRoute = route.name === 'UserOtp'
  const isBasicDetailsRoute = route.name === 'UserBasicDetails'
  const isPanDetailsRoute = route.name === 'UserPanDetails'
  const { mutate, isLoading: isContinueLoading } = useAddCustomerNextStateWeb()
  const { data: agentData, isLoading } = useAgentDetailsWF()
  const { data: bannerData } = useGetZetCashMarketplaceBanner({
    onecode: agentData?.onecode || '',
  })
  const { data: criteriaData } = useGetCriteriaIndexes({
    hash: encodeURIComponent(getLoginHash() || ''),
    locale: i18n.locale,
  })
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [focusedField, setFocusedField] = useState<
    'fullName' | 'mobileNumber' | 'email' | 'panNumber' | null
  >(null)
  const [isOtpStep, setIsOtpStep] = useState(isOtpRoute)
  const [otpValue, setOtpValue] = useState({})
  const [timer, setTimer] = useState(30)
  const [otpFocusIndex, setOtpFocusIndex] = useState(0)
  const otpInputRefs = useRef<Record<number, any>>({})
  const [otpCustomerId, setOtpCustomerId] = useState<number | undefined>()
  const [otpPhoneNumber, setOtpPhoneNumber] = useState<string | undefined>()
  const [otpFullName, setOtpFullName] = useState<string | undefined>()
  const [isOtpConsentChecked, setIsOtpConsentChecked] = useState(false)
  const { setCustomerName, setCustomerPhoneNumber } = useAddCustomerStore(
    (state) => ({
      setCustomerName: state.setCustomerName,
      setCustomerPhoneNumber: state.setCustomerPhoneNumber,
    }),
  )

  const mode = getUserFlowMode(agentData)
  const isZetPro = useMemo(() => {
    return zetProFixedList.some(
      (id) => Number(id) === Number(agentData?.onecodeId),
    )
  }, [agentData?.onecodeId])
  const shouldShowEmail = !isZetPro

  const validationSchema = useMemo(
    () =>
      z.object({
        fullName: z
          .string()
          .trim()
          .min(1, { message: NAME_FIELD_EMPTY })
          .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
        mobileNumber: z
          .string()
          .min(1, { message: NAME_FIELD_EMPTY })
          .regex(
            new RegExp(MOBILE_NUMBER_REGEX),
            'Invalid Number. Please check information.',
          )
          .max(10, { message: 'Invalid Number. Please check information.' }),
        email: shouldShowEmail
          ? z.string().email({ message: 'Please enter valid email address' })
          : z.string().optional(),
      }),
    [shouldShowEmail],
  )

  const initialValues = getUserFlowInitialValues(agentData)

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof validationSchema>>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    defaultValues: initialValues,
  })
  const {
    control: basicDetailsControl,
    handleSubmit: handleBasicDetailsSubmit,
    setValue: setBasicDetailsValue,
    watch: watchBasicDetails,
    formState: { errors: basicDetailsErrors, isValid: isBasicDetailsValid },
  } = useForm<z.infer<typeof basicDetailsValidationSchema>>({
    resolver: zodResolver(basicDetailsValidationSchema),
    mode: 'onChange',
    defaultValues: {
      occupation: BASIC_DETAILS_OCCUPATION.SALARIED,
      monthlyIncome: '',
      pinCode: '',
    },
  })
  const {
    control: panDetailsControl,
    handleSubmit: handlePanDetailsSubmit,
    setValue: setPanDetailsValue,
    formState: { errors: panDetailsErrors, isValid: isPanDetailsValid },
  } = useForm<z.infer<typeof panDetailsValidationSchema>>({
    resolver: zodResolver(panDetailsValidationSchema),
    mode: 'onChange',
    defaultValues: {
      panNumber: '',
      dob: '',
    },
  })
  const { data: partnerProducts = [] } = useUserFlowPartnerProducts()
  const partnerCards = useMemo<PartnerCarouselCard[]>(() => {
    const seen = new Set<string>()

    return partnerProducts
      .filter((product) => product?.imageUrl)
      .filter((product) => {
        const key = `${product?.partnerId || ''}-${product?.imageUrl || ''}`

        if (seen.has(key)) {
          return false
        }

        seen.add(key)
        return true
      })
      .map((product) => ({
        key: `${product?.partnerId || product?.id || product?.imageUrl}`,
        logo: product.imageUrl || '',
        width: getPartnerLogoWidth(product?.name),
      }))
  }, [partnerProducts])
  useEffect(() => {
    setIsOtpStep(isOtpRoute)
  }, [isOtpRoute])

  useEffect(() => {
    if (isBasicDetailsRoute) {
      setUserFlowSession({
        active: true,
        lastRoute: 'UserBasicDetails',
      })
    }
  }, [isBasicDetailsRoute])

  useEffect(() => {
    if (isPanDetailsRoute) {
      setUserFlowSession({
        active: true,
        lastRoute: 'UserPanDetails',
      })
    }
  }, [isPanDetailsRoute])

  useEffect(() => {
    if (!isOtpRoute) {
      return
    }

    const session = getUserFlowSession()
    setOtpCustomerId(route.params?.customerId || session.customerId)
    setOtpPhoneNumber(route.params?.customerPhoneNumber || session.phoneNumber)
    setOtpFullName(route.params?.customerName || session.fullName)
  }, [isOtpRoute, route.params])

  useEffect(() => {
    setUserFlowSession({
      ...getUserFlowSession(),
      active: true,
    })
  }, [])

  useEffect(() => {
    if (route.name !== 'UserLogin') {
      return
    }

    const state = navigation.getState()
    const isStandaloneUserLogin =
      state.routes.length === 1 && state.routes[0]?.name === route.name

    if (!isStandaloneUserLogin) {
      navigation.reset({
        index: 0,
        routes: [{ name: route.name, params: route.params }],
      })
    }
  }, [navigation, route.name, route.params])

  useEffect(() => {
    if (agentData) {
      setValue('fullName', initialValues.fullName)
      setValue('mobileNumber', initialValues.mobileNumber)
      setValue('email', initialValues.email)
    }
  }, [
    agentData,
    initialValues.email,
    initialValues.fullName,
    initialValues.mobileNumber,
    setValue,
  ])

  useEffect(() => {
    if (route.name !== 'UserLogin') {
      return
    }

    if (
      typeof window !== 'undefined' &&
      window.location.pathname === '/user/login'
    ) {
      return
    }

    if (hasUserFlowLoginSession()) {
      const session = getUserFlowSession()
      const nextRoute =
        session.lastRoute && session.lastRoute !== 'UserOffers'
          ? session.lastRoute
          : getUserFlowFallbackRoute(mode)

      navigation.reset({
        index: 0,
        routes: [
          {
            name: nextRoute,
            params: {
              customerId: session.customerId,
            },
          },
        ],
      })
    }
  }, [mode, navigation, route.name])

  useEffect(() => {
    if (!isOtpStep) {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      return
    }

    setTimer(30)
    timerRef.current = setInterval(() => {
      setTimer((current) => {
        if (current <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current)
          }
          return 0
        }

        return current - 1
      })
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isOtpStep])

  useEffect(() => {
    if (!isOtpStep) {
      return
    }

    setOtpFocusIndex(0)

    const focusTimeout = setTimeout(() => {
      otpInputRefs.current[0]?.focus?.()
    }, 0)

    return () => clearTimeout(focusTimeout)
  }, [isOtpStep])

  const session = getUserFlowSession()
  const resolvedCustomerId =
    route.params?.customerId || otpCustomerId || session.customerId
  const resolvedPhoneNumber =
    route.params?.customerPhoneNumber || otpPhoneNumber || session.phoneNumber
  const resolvedFullName =
    route.params?.customerName || otpFullName || session.fullName
  const panDobMaxDate = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 15)

    const year = date.getFullYear()
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')

    return `${year}-${month}-${day}`
  }, [])
  const selectedOccupation = watchBasicDetails('occupation')
  const selectedOccupationField = criteriaData
    ?.find((item) => item.criteriaName === 'employmentType')
    ?.fields?.find(
      (item) =>
        normalizeOccupationValue(item?.displayValue) ===
          normalizeOccupationValue(selectedOccupation) ||
        normalizeOccupationValue(item?.value) ===
          normalizeOccupationValue(selectedOccupation),
    )

  useEffect(() => {
    if (isBasicDetailsRoute && !resolvedCustomerId && session?.lastRoute) {
      navigation.goBack()
    }
  }, [isBasicDetailsRoute, navigation, resolvedCustomerId, session?.lastRoute])

  useEffect(() => {
    if (isPanDetailsRoute && !resolvedCustomerId && session?.lastRoute) {
      navigation.goBack()
    }
  }, [isPanDetailsRoute, navigation, resolvedCustomerId, session?.lastRoute])

  const onSubmit = () => {
    const fullName = getValues('fullName').trim()
    const phoneNumber = getValues('mobileNumber')
    const email = getValues('email')?.trim()

    setCustomerName(fullName)
    setCustomerPhoneNumber(phoneNumber)
    setUserFlowSession({
      active: true,
      fullName,
      phoneNumber,
      ...(email ? { email } : {}),
      mode,
      lastRoute: 'UserLogin',
    })

    mutate(
      {
        fullName,
        phoneNumber,
        ...(shouldShowEmail && email ? { email } : {}),
        customerIntent: USER_LOGIN_CUSTOMER_INTENT,
        hash: encodeURIComponent(getLoginHash() || ''),
        categoryId: agentData?.categoryId,
        isVerifyOtpOnly: true,
        screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
        customerAdditionSource: agentData?.customerAdditionSource,
      },
      {
        onSuccess: (data: any) => {
          if (data?.action !== 'SHOW_OTP_SCREEN') {
            return
          }

          const customerId = data?.actionData?.customerId
          setOtpCustomerId(customerId)
          setOtpPhoneNumber(phoneNumber)
          setOtpFullName(fullName)
          setUserFlowSession({
            active: true,
            customerId,
            fullName,
            phoneNumber,
            lastRoute: 'UserOtp',
          })
          setOtpValue({})
          setOtpFocusIndex(0)
          navigation.navigate(
            'UserOtp' as never,
            {
              customerId,
              customerPhoneNumber: phoneNumber,
              customerName: fullName,
            } as never,
          )
        },
      },
    )
  }

  const otp = Object.values(otpValue).join('')

  const onVerifyOtp = () => {
    setUserFlowSession({
      active: true,
      customerId: Number(otpCustomerId || resolvedCustomerId),
      fullName: otpFullName || resolvedFullName,
      phoneNumber: otpPhoneNumber || resolvedPhoneNumber,
      lastRoute: 'UserOtp',
    })

    mutate({
      customerId: Number(otpCustomerId || resolvedCustomerId),
      phoneNumber: otpPhoneNumber || resolvedPhoneNumber,
      otp,
      screenName: 'SHOWN_OTP_SCREEN',
    })
  }

  const onSubmitBasicDetails = ({
    occupation,
    monthlyIncome,
    pinCode,
  }: z.infer<typeof basicDetailsValidationSchema>) => {
    const employmentTypeField = criteriaData
      ?.find((item) => item.criteriaName === 'employmentType')
      ?.fields?.find(
        (item) =>
          normalizeOccupationValue(item?.displayValue) ===
            normalizeOccupationValue(occupation) ||
          normalizeOccupationValue(item?.value) ===
            normalizeOccupationValue(occupation),
      )

    mutate({
      customerId: Number(resolvedCustomerId),
      phoneNumber: resolvedPhoneNumber,
      fullName: resolvedFullName,
      modeOfIncome: 1,
      pinCode,
      annualIncome: monthlyIncome.replace(/,/g, ''),
      employmentType: String(employmentTypeField?.key || ''),
      screenName: USER_BASIC_DETAILS_SCREEN,
    } as any)
  }

  const onSubmitPanDetails = ({
    panNumber,
    dob,
  }: z.infer<typeof panDetailsValidationSchema>) => {
    mutate({
      customerId: Number(resolvedCustomerId),
      panNo: panNumber,
      dob,
      screenName: USER_PAN_DETAILS_SCREEN,
    } as any)
  }

  const onResendOtp = () => {
    setOtpValue({})
    setTimer(30)
    setOtpFocusIndex(0)
    mutate({
      fullName: otpFullName,
      phoneNumber: otpPhoneNumber,
      customerIntent: USER_LOGIN_CUSTOMER_INTENT,
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      isVerifyOtpOnly: true,
    })
  }

  const handleOtpChange = (text: string, index: number) => {
    const nextDigit = text.slice(-1)

    if (!/^\d?$/.test(nextDigit)) {
      return
    }

    const nextValue = { ...otpValue, [index]: nextDigit }
    setOtpValue(nextValue)

    if (nextDigit && index < OTP_LENGTH - 1) {
      const nextIndex = index + 1
      setOtpFocusIndex(nextIndex)
      otpInputRefs.current[nextIndex]?.focus?.()
    }

    const otpDigits = Array.from({ length: OTP_LENGTH }, (_, otpIndex) =>
      String(nextValue[otpIndex] || ''),
    )
    if (otpDigits.every(Boolean)) {
      Keyboard.dismiss()
    }
  }

  const handleOtpKeyPress = (key: string, index: number) => {
    if (key !== 'Backspace') return

    if (otpValue[index]) {
      setOtpValue({ ...otpValue, [index]: '' })
      return
    }

    if (index > 0) {
      const previousIndex = index - 1
      setOtpFocusIndex(previousIndex)
      setOtpValue({ ...otpValue, [previousIndex]: '' })
      otpInputRefs.current[previousIndex]?.focus?.()
    }
  }

  const handleEditOtpStep = () => {
    setOtpValue({})
    setOtpFocusIndex(0)
    navigation.goBack()
  }

  const openTermsAndConditions = () => {
    window.open(termsAndConditionsLink)
  }

  const openPrivacyPolicy = () => {
    window.open(privacyPolicyLink)
  }

  if (isLoading || !agentData) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ActivityIndicator size="large" color="#1D4ED8" />
      </Stack>
    )
  }

  return (
    <ScreenViewEventWrapper
      screenName={
        isPanDetailsRoute
          ? 'user_flow_pan_details'
          : isBasicDetailsRoute
            ? 'user_flow_basic_details'
            : 'user_flow_login'
      }
    >
      <ScrollView
        className="flex-1 bg-white"
        // contentContainerStyle={styles.pageScrollContent}
        showsVerticalScrollIndicator={false}
      >
        <Stack style={styles.pageContent}>
          <HeroBanner bannerUrl={bannerData?.imageUrl} />
          <Stack className="bg-white px-4 pb-10 pt-4">
            {!isOtpStep && !isBasicDetailsRoute && !isPanDetailsRoute ? (
              <>
                <Stack>
                  <Controller
                    control={control}
                    name="fullName"
                    render={({ field: { onChange, value } }) => (
                      <Stack className="mb-4">
                        <Text style={styles.formLabelTight}>
                          Full Name{' '}
                          <Text style={styles.requiredAsterisk}>*</Text>
                        </Text>
                        <Input
                          placeholder="eg. Abhinav Kumar"
                          value={value}
                          onChangeText={onChange}
                          onFocus={() => setFocusedField('fullName')}
                          onBlur={() => setFocusedField(null)}
                          type={errors?.fullName?.message ? 'error' : 'primary'}
                          showErrorText={errors?.fullName?.message}
                          errorTextStyle={styles.errorText}
                          frameStyle={getInputFrameStyle(
                            focusedField === 'fullName',
                          )}
                        />
                      </Stack>
                    )}
                  />

                  <Controller
                    control={control}
                    name="mobileNumber"
                    render={({ field: { onChange, value } }) => (
                      <Stack className="mb-4">
                        <Text style={styles.formLabel}>
                          Phone Number{' '}
                          <Text style={styles.requiredAsterisk}>*</Text>
                        </Text>
                        <Input
                          className={violetFocusInputClassName}
                          placeholder="99999 99999"
                          keyboardType="number-pad"
                          maxLength={10}
                          value={value}
                          onChangeText={onChange}
                          onFocus={() => setFocusedField('mobileNumber')}
                          onBlur={() => setFocusedField(null)}
                          type={
                            errors?.mobileNumber?.message ? 'error' : 'primary'
                          }
                          iconLeft={
                            <Image
                              source={{ uri: userLoginPhoneIcon }}
                              style={styles.phoneInputIcon}
                              resizeMode="contain"
                            />
                          }
                          frameStyle={getInputFrameStyle(
                            focusedField === 'mobileNumber',
                          )}
                        />
                        {errors?.mobileNumber?.message ? (
                          <B5 className="mt-2" style={styles.errorText}>
                            {errors.mobileNumber.message}
                          </B5>
                        ) : null}
                      </Stack>
                    )}
                  />

                  {shouldShowEmail ? (
                    <Controller
                      control={control}
                      name="email"
                      render={({ field: { onChange, value } }) => (
                        <Stack>
                          <Text style={styles.formLabel}>
                            Email <Text style={styles.requiredAsterisk}>*</Text>
                          </Text>
                          <Input
                            placeholder="eg. abhinav@example.com"
                            keyboardType="email-address"
                            value={value}
                            onChangeText={onChange}
                            onFocus={() => setFocusedField('email')}
                            onBlur={() => setFocusedField(null)}
                            type={errors?.email?.message ? 'error' : 'primary'}
                            frameStyle={getInputFrameStyle(
                              focusedField === 'email',
                            )}
                          />
                          {errors?.email?.message ? (
                            <B5 className="mt-2" style={styles.errorText}>
                              {errors.email.message}
                            </B5>
                          ) : null}
                        </Stack>
                      )}
                    />
                  ) : null}
                </Stack>

                <Button
                  className="mt-8"
                  size="lg"
                  disabled={!isValid}
                  loading={isContinueLoading}
                  onPress={handleSubmit(onSubmit)}
                  style={primaryButtonStyle}
                  textStyle="font-[THICCCBOI-SemiBold]"
                >
                  Apply Now
                </Button>

                <Stack className="mt-6">
                  <XStack className="items-center gap-4">
                    <Stack style={styles.dividerLine} />
                    <Text style={styles.partnersTitle}>Our Partners</Text>
                    <Stack style={styles.dividerLine} />
                  </XStack>

                  {partnerCards.length ? (
                    <Stack
                      className="mt-4 overflow-hidden"
                      style={styles.marqueeContainer}
                    >
                      <Marquee duration={36000} className="overflow-hidden">
                        <XStack style={styles.marqueeTrack}>
                          {partnerCards.map((partner, index) => (
                            <PartnerCard
                              key={`${partner.key}-${index}`}
                              logo={partner.logo}
                              width={partner.width}
                            />
                          ))}
                        </XStack>
                      </Marquee>
                    </Stack>
                  ) : null}
                </Stack>
              </>
            ) : isBasicDetailsRoute ? (
              <Stack className="gap-[18px] pb-10">
                <Stack style={styles.firstFieldSection}>
                  <Text style={styles.fieldLabel}>
                    Occupation{' '}
                    <Text style={styles.requiredAsteriskSoft}>*</Text>
                  </Text>
                  <XStack style={styles.occupationRow}>
                    {[
                      BASIC_DETAILS_OCCUPATION.SALARIED,
                      BASIC_DETAILS_OCCUPATION.SELF_EMPLOYED,
                    ].map((occupation) => {
                      const isSelected = selectedOccupation === occupation

                      return (
                        <Pressable
                          key={occupation}
                          onPress={() =>
                            setBasicDetailsValue('occupation', occupation, {
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }
                          style={getOccupationCardStyle(isSelected)}
                        >
                          <XStack style={styles.occupationCardContent}>
                            <Text
                              style={[
                                styles.occupationText,
                                isSelected
                                  ? styles.occupationTextSelected
                                  : styles.occupationTextDefault,
                              ]}
                            >
                              {occupation}
                            </Text>
                            <Image
                              source={{
                                uri:
                                  occupation ===
                                  BASIC_DETAILS_OCCUPATION.SALARIED
                                    ? CUSTOMER_DETAILS_OCCUPATION_ICONS.salaried
                                    : CUSTOMER_DETAILS_OCCUPATION_ICONS.selfEmployed,
                              }}
                              style={styles.occupationIcon}
                              resizeMode="contain"
                            />
                          </XStack>
                        </Pressable>
                      )
                    })}
                  </XStack>
                  {basicDetailsErrors.occupation?.message ? (
                    <B5 className="mt-2" style={styles.errorText}>
                      {basicDetailsErrors.occupation.message}
                    </B5>
                  ) : null}
                </Stack>

                <Stack style={styles.fieldSection}>
                  <Text style={styles.formLabel}>
                    Monthly Income{' '}
                    <Text style={styles.requiredAsteriskSoft}>*</Text>
                  </Text>
                  <Controller
                    control={basicDetailsControl}
                    name="monthlyIncome"
                    render={({ field: { value } }) => (
                      <Input
                        className={violetFocusInputClassName}
                        placeholder="20,000"
                        keyboardType="number-pad"
                        value={formatIncomeValue(value || '')}
                        onChangeText={(text) =>
                          setBasicDetailsValue(
                            'monthlyIncome',
                            text.replace(/\D/g, ''),
                            {
                              shouldTouch: true,
                              shouldValidate: true,
                            },
                          )
                        }
                        iconLeft={
                          <Image
                            source={{ uri: userLoginRupeeIcon }}
                            style={styles.rupeeInputIcon}
                            resizeMode="contain"
                          />
                        }
                        type={
                          basicDetailsErrors.monthlyIncome?.message
                            ? 'error'
                            : 'primary'
                        }
                        frameStyle={compactInputFrameBaseStyle}
                      />
                    )}
                  />
                  {basicDetailsErrors.monthlyIncome?.message ? (
                    <B5 className="mt-2" style={styles.errorText}>
                      {basicDetailsErrors.monthlyIncome.message}
                    </B5>
                  ) : null}
                </Stack>

                <Stack style={styles.fieldSection}>
                  <Text style={styles.formLabel}>
                    Pin Code <Text style={styles.requiredAsteriskSoft}>*</Text>
                  </Text>
                  <Controller
                    control={basicDetailsControl}
                    name="pinCode"
                    render={({ field: { value } }) => (
                      <Input
                        className={violetFocusInputClassName}
                        placeholder="416510"
                        keyboardType="number-pad"
                        maxLength={6}
                        value={value}
                        onChangeText={(text) =>
                          setBasicDetailsValue(
                            'pinCode',
                            text.replace(/\D/g, ''),
                            {
                              shouldTouch: true,
                              shouldValidate: true,
                            },
                          )
                        }
                        type={
                          basicDetailsErrors.pinCode?.message
                            ? 'error'
                            : 'primary'
                        }
                        frameStyle={compactInputFrameBaseStyle}
                      />
                    )}
                  />
                  {basicDetailsErrors.pinCode?.message ? (
                    <B5 className="mt-2" style={styles.errorText}>
                      {basicDetailsErrors.pinCode.message}
                    </B5>
                  ) : null}
                </Stack>

                <Button
                  className="mt-6"
                  size="lg"
                  disabled={
                    !isBasicDetailsValid ||
                    !resolvedCustomerId ||
                    !selectedOccupationField?.key
                  }
                  loading={isContinueLoading}
                  onPress={handleBasicDetailsSubmit(onSubmitBasicDetails)}
                  style={primaryButtonStyle}
                  textStyle="font-[THICCCBOI-SemiBold]"
                >
                  Continue
                </Button>
              </Stack>
            ) : isPanDetailsRoute ? (
              <Stack className="gap-[18px] pb-10">
                <Stack style={styles.firstFieldSection}>
                  <Text style={styles.formLabel}>
                    PAN Number{' '}
                    <Text style={styles.requiredAsteriskSoft}>*</Text>
                  </Text>
                  <Controller
                    control={panDetailsControl}
                    name="panNumber"
                    render={({ field: { value } }) => (
                      <Input
                        className={violetFocusInputClassName}
                        placeholder="Eg: NVLNK4546"
                        value={value}
                        autoCapitalize="characters"
                        maxLength={10}
                        onFocus={() => setFocusedField('panNumber')}
                        onBlur={() => setFocusedField(null)}
                        onChangeText={(text) =>
                          setPanDetailsValue('panNumber', text.toUpperCase(), {
                            shouldTouch: true,
                            shouldValidate: true,
                          })
                        }
                        type={
                          panDetailsErrors.panNumber?.message
                            ? 'error'
                            : 'primary'
                        }
                        frameStyle={{
                          ...compactInputFrameBaseStyle,
                          borderColor:
                            focusedField === 'panNumber'
                              ? '#832DC2'
                              : '#E1DFE5',
                        }}
                      />
                    )}
                  />
                  {panDetailsErrors.panNumber?.message ? (
                    <B5 className="mt-2" style={styles.errorText}>
                      {panDetailsErrors.panNumber.message}
                    </B5>
                  ) : null}
                </Stack>

                <Stack style={styles.fieldSection}>
                  <Text style={styles.formLabel}>
                    Date of Birth{' '}
                    <Text style={styles.requiredAsteriskSoft}>*</Text>
                  </Text>
                  <Controller
                    control={panDetailsControl}
                    name="dob"
                    render={({ field: { value } }) => (
                      <Stack>
                        <Icon
                          type="CalenderOutlined"
                          color="#8F80A1"
                          size={22}
                          style={styles.webDatePickerIcon}
                        />
                        <WebDatePicker
                          value={value}
                          maxDate={panDobMaxDate}
                          focusBorderColor="#832DC2"
                          showCustomPlaceholder
                          placeholder="YYYY-MM-DD"
                          placeholderStyle={styles.webDatePickerPlaceholder}
                          style={styles.webDatePickerInput}
                          onBlur={() =>
                            setPanDetailsValue('dob', value || '', {
                              shouldTouch: true,
                              shouldValidate: true,
                            })
                          }
                          onChange={(event) =>
                            setPanDetailsValue(
                              'dob',
                              event?.target?.value || '',
                              {
                                shouldTouch: true,
                                shouldValidate: true,
                              },
                            )
                          }
                        />
                      </Stack>
                    )}
                  />
                  {panDetailsErrors.dob?.message ? (
                    <B5 className="mt-2" style={styles.errorText}>
                      {panDetailsErrors.dob.message}
                    </B5>
                  ) : null}
                </Stack>

                <Button
                  className="mt-6"
                  size="lg"
                  disabled={!isPanDetailsValid || !resolvedCustomerId}
                  loading={isContinueLoading}
                  onPress={handlePanDetailsSubmit(onSubmitPanDetails)}
                  style={primaryButtonStyle}
                  textStyle="font-[THICCCBOI-SemiBold]"
                >
                  Continue
                </Button>
              </Stack>
            ) : (
              <Stack className="pb-10">
                <XStack className="items-center gap-1">
                  <Text style={styles.otpMetaText}>{'OTP was sent to '}</Text>
                  <Text style={styles.otpPhoneText}>
                    {otpPhoneNumber || getValues('mobileNumber') || ''}
                  </Text>
                  <Pressable onPress={handleEditOtpStep}>
                    <Text style={styles.otpEditText}>Edit</Text>
                  </Pressable>
                </XStack>

                <XStack className="mt-4 gap-2">
                  {Array.from({ length: OTP_LENGTH }).map((_, index) => {
                    const isFocused = otpFocusIndex === index
                    return (
                      <Input
                        className={otpFocusInputClassName}
                        key={index}
                        ref={(ref) => {
                          otpInputRefs.current[index] = ref
                        }}
                        value={String(otpValue[index] || '')}
                        keyboardType="number-pad"
                        maxLength={1}
                        onFocus={() => setOtpFocusIndex(index)}
                        onChangeText={(text) => handleOtpChange(text, index)}
                        onKeyPress={(event) =>
                          handleOtpKeyPress(event.nativeEvent.key, index)
                        }
                        frameStyle={getOtpInputFrameStyle(isFocused)}
                        style={styles.otpInputText}
                      />
                    )
                  })}
                </XStack>

                {timer > 0 ? (
                  <XStack className="mt-5 items-center gap-1">
                    <Text style={styles.otpResendText}>Resend OTP in</Text>
                    <Text style={styles.otpTimerText}>{timer} sec</Text>
                  </XStack>
                ) : (
                  <XStack className="mt-5 items-center gap-1">
                    <Text style={styles.otpResendText}>
                      Didn’t receive the code?
                    </Text>
                    <Pressable onPress={onResendOtp}>
                      <Text style={styles.otpResendActionText}>Resend OTP</Text>
                    </Pressable>
                  </XStack>
                )}

                <XStack className="mt-5 gap-2">
                  <Pressable
                    onPress={() =>
                      setIsOtpConsentChecked((current) => !current)
                    }
                  >
                    <Icon
                      type={
                        isOtpConsentChecked ? 'CheckBoxTickFilled' : 'CheckBox'
                      }
                      color="#832DC2"
                      size={24}
                    />
                  </Pressable>
                  <Text style={styles.otpConsentText}>
                    {'By continuing, I agree to the '}
                    <Text
                      style={styles.linkText}
                      onPress={openTermsAndConditions}
                    >
                      T&Cs
                    </Text>
                    {' & '}
                    <Text style={styles.linkText} onPress={openPrivacyPolicy}>
                      Privacy Policy
                    </Text>
                    {' of\nZET & grant access to my credit score and cKYC'}
                  </Text>
                </XStack>

                <Button
                  className="mt-6"
                  size="lg"
                  disabled={otp.length !== OTP_LENGTH || !isOtpConsentChecked}
                  loading={isContinueLoading}
                  onPress={onVerifyOtp}
                  style={primaryButtonStyle}
                  textStyle="font-[THICCCBOI-SemiBold]"
                >
                  Verify OTP
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </ScrollView>
    </ScreenViewEventWrapper>
  )
}

const HeroBanner = ({ bannerUrl }: { bannerUrl?: string }) => {
  return (
    <Stack>
      <Image
        style={{
          width: '100%',
          aspectRatio: 1.62,
        }}
        source={{
          uri: bannerUrl,
        }}
        resizeMode="cover"
      />
    </Stack>
  )
}

const PartnerCard = ({ logo, width }: { logo?: string; width?: number }) => {
  return (
    <Stack style={styles.partnerCard}>
      {logo ? (
        <Image
          source={{ uri: logo }}
          style={getPartnerLogoStyle(width)}
          resizeMode="contain"
        />
      ) : null}
    </Stack>
  )
}

const styles = StyleSheet.create({
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#EFECF2',
  },
  errorText: {
    fontFamily: 'THICCCBOI-Regular',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: -0.24,
    color: '#D30D28',
  },
  fieldLabel: {
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: -0.28,
    color: '#7A6F86',
  },
  firstFieldSection: {
    marginTop: 10,
  },
  formLabel: {
    marginBottom: 4,
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: -0.28,
    color: '#7A6F86',
  },
  formLabelTight: {
    marginBottom: 2,
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: -0.28,
    color: '#7A6F86',
  },
  fieldSection: {
    marginTop: 16,
  },
  heroBannerContainer: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    alignSelf: 'stretch',
  },
  heroBannerImage: {
    width: '100%',
    height: 430,
  },
  inputFrameDefault: {
    borderColor: '#E5E7EB',
  },
  inputFrameFocused: {
    borderColor: '#832DC2',
  },
  linkText: {
    color: '#832DC2',
  },
  marqueeContainer: {
    marginRight: -16,
  },
  marqueeTrack: {
    flexDirection: 'row',
  },
  occupationCard: {
    flex: 1,
    minHeight: 48,
    borderRadius: 14,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
    justifyContent: 'center',
  },
  occupationCardDefault: {
    borderColor: '#E1DFE5',
    backgroundColor: '#FBFAFC',
  },
  occupationCardSelected: {
    borderColor: '#7929B4',
    backgroundColor: '#FBF3FF',
  },
  occupationCardContent: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  occupationIcon: {
    width: 24,
    height: 24,
  },
  occupationRow: {
    gap: 12,
    marginTop: 6,
  },
  occupationText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    letterSpacing: -0.28,
  },
  occupationTextDefault: {
    fontFamily: 'THICCCBOI-Medium',
    color: '#17061F',
  },
  occupationTextSelected: {
    fontFamily: 'THICCCBOI-SemiBold',
    color: '#410F65',
  },
  otpConsentText: {
    flex: 1,
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 12,
    lineHeight: 18,
    letterSpacing: -0.24,
    color: '#7A6F86',
  },
  otpEditText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#832DC2',
    textDecorationLine: 'underline',
  },
  otpInputFrame: {
    minHeight: 58,
    width: 62,
    minWidth: 62,
    borderRadius: 16,
    borderWidth: 1,
    backgroundColor: '#FFFFFF',
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: 'center',
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#141216',
    outlineStyle: 'none',
    outlineWidth: 0,
    boxShadow: 'none',
  },
  otpInputFrameDefault: {
    borderColor: '#E5E7EB',
  },
  otpInputFrameFocused: {
    borderColor: '#A855F7',
  },
  otpInputText: {
    textAlign: 'center',
  },
  otpMetaText: {
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#7A6F86',
  },
  otpPhoneText: {
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#141216',
  },
  otpResendActionText: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#832DC2',
  },
  otpResendText: {
    fontFamily: 'THICCCBOI-Medium',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#3A3946',
  },
  otpTimerText: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 16,
    lineHeight: 22,
    letterSpacing: -0.16,
    color: '#000000',
  },
  partnerCard: {
    width: partnerCardWidth,
    minWidth: partnerCardWidth,
    borderRadius: 14.4,
    borderWidth: 1.2,
    borderColor: '#EFECF2',
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 12,
    paddingVertical: 12,
    minHeight: partnerCardHeight,
    marginRight: partnerCardGap,
    alignItems: 'center',
    justifyContent: 'center',
  },
  partnerLogo: {
    height: 24,
  },
  partnerLogoDefaultWidth: {
    width: 84,
  },
  pageContent: {
    width: '100%',
    maxWidth: 640,
    alignSelf: 'center',
  },
  pageScrollContent: {
    paddingBottom: 24,
  },
  phoneInputIcon: {
    width: 20,
    height: 20,
  },
  rupeeInputIcon: {
    width: 12,
    height: 12,
    marginLeft: 8,
  },
  webDatePickerIcon: {
    position: 'absolute',
    left: 16,
    top: 13,
    zIndex: 2,
  },
  webDatePickerInput: {
    backgroundColor: '#FBFAFC',
    borderColor: '#E1DFE5',
    borderRadius: 14,
    borderWidth: 1,
    color: '#141216',
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 16,
    height: 48,
    lineHeight: 22,
    paddingLeft: 52,
    paddingRight: 16,
    paddingTop: 10,
    outlineStyle: 'none',
    outlineWidth: 0,
    boxShadow: 'none',
  },
  webDatePickerPlaceholder: {
    color: '#94A3B8',
    fontFamily: 'THICCCBOI-SemiBold',
    fontSize: 16,
    left: 52,
    top: 12,
    letterSpacing: -0.16,
  },
  formContent: {
    backgroundColor: '#FFFFFF',
    paddingTop: 16,
    paddingBottom: 40,
  },
  partnersTitle: {
    fontFamily: 'THICCCBOI-ExtraBold',
    fontSize: 15,
    lineHeight: 20,
    letterSpacing: -0.3,
    color: '#374151',
  },
  requiredAsterisk: {
    color: '#D30D28',
  },
  requiredAsteriskSoft: {
    color: '#D30D28',
  },
})

export default UserLogin
